// SCR-003: the customer list — search and a status filter in one compact bar, a table of names
// with this month's billing and the last 30 days' usage (one list call each, never one per
// customer), and the Add customer dialog (API-006, REQ-042, REQ-013).
import { useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { useCustomerDirectory } from "../../ui/customer-directory";
import { Dialog } from "@nexus/ui";
import { errorId, Field, LabelText } from "@nexus/ui";
import { hasErrors, type FieldErrors } from "@nexus/ui";
import { Icon } from "@nexus/ui";
import { currentMonth } from "@nexus/ui";
import { Chip, EmptyState, FilterBar, NameCell, PageHeader, Segmented } from "@nexus/ui";
import { DataTable, Money, type Column } from "@nexus/ui";
import {
  compareBilled,
  compareNullable,
  customerPath,
  customerRows,
  filterByStatus,
  filterOrgs,
  formatCount,
  newOrgRequest,
  orgFormServerError,
  orgStatusChip,
  sinceMonth,
  statusCounts,
  statusFilterOptions,
  usageWindow,
  validateNewOrg,
  type CustomerRow,
  type NewOrgValues,
  type Org,
  type OrgUsageItem,
  type RevenueBreakdown,
  type StatusFilter,
} from "./model";

const EMPTY_FORM: NewOrgValues = { id: "", slug: "", name: "" };

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

export function CustomerList() {
  const client = useApi();
  const directory = useCustomerDirectory();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [usage, setUsage] = useState<OrgUsageItem[] | null>(null);
  const [revenue, setRevenue] = useState<RevenueBreakdown | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewOrgValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const load = (after: string | null, replace: boolean) => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/admin/orgs", { query: after ? { cursor: after, limit: 500 } : { limit: 500 } })
      .then((page) => {
        setOrgs((current) => (replace ? page.items : [...current, ...page.items]));
        setCursor(page.nextCursor);
        directory.remember(page.items.map((org) => ({ id: org.id, name: org.name })));
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(null, true);
    // The two extra columns are one report each; a failure leaves their cells as "—".
    const { from, to } = usageWindow(new Date());
    client
      .get("/v1/admin/usage/by-org", { query: { from, to } })
      .then((report) => setUsage(report.items))
      .catch(() => setUsage(null));
    const month = currentMonth(new Date());
    client
      .get("/v1/admin/revenue/by-org", { query: { from: month, to: month } })
      .then(setRevenue)
      .catch(() => setRevenue(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const closeDialog = () => {
    setOpen(false);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const submit = () => {
    const validation = validateNewOrg(form);
    if (hasErrors(validation)) {
      setErrors(validation);
      return;
    }
    const body = newOrgRequest(form);
    if (!body) return;
    setSubmitting(true);
    client
      .post("/v1/admin/orgs", { body })
      .then((org) => {
        setOrgs((current) => [org, ...current]);
        directory.remember([{ id: org.id, name: org.name }]);
        closeDialog();
      })
      .catch((err: unknown) => setErrors(orgFormServerError(unexpected(err))))
      .finally(() => setSubmitting(false));
  };

  const counts = statusCounts(orgs);
  const rows = customerRows(filterByStatus(filterOrgs(orgs, search), status), usage, revenue);

  const columns: Column<CustomerRow>[] = [
    {
      key: "name",
      header: "Customer",
      cell: (row) => <NameCell name={row.org.name} id={row.org.id} href={customerPath(row.org.id)} />,
      compare: (a, b) => a.org.name.localeCompare(b.org.name),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => {
        const chip = orgStatusChip(row.org.status);
        return <Chip tone={chip.tone}>{chip.label}</Chip>;
      },
      compare: (a, b) => a.org.status.localeCompare(b.org.status),
    },
    {
      key: "requests",
      header: "Requests, 30 days",
      numeric: true,
      help: "customers.requests",
      cell: (row) => (row.requests === null ? "—" : formatCount(row.requests)),
      compare: (a, b) => compareNullable(a.requests, b.requests),
    },
    {
      key: "tokens",
      header: "Tokens, 30 days",
      numeric: true,
      help: "customers.tokens",
      cell: (row) => (row.tokens === null ? "—" : formatCount(row.tokens)),
      compare: (a, b) => compareNullable(a.tokens, b.tokens),
    },
    {
      key: "billed",
      header: "Billed this month",
      numeric: true,
      help: "customers.billedThisMonth",
      cell: (row) =>
        row.billed.kind === "billed" ? <Money micro={row.billed.micro} /> : row.billed.kind === "not-billed" ? <span class="muted">Not billed</span> : "—",
      compare: (a, b) => compareBilled(a.billed, b.billed),
    },
    {
      key: "since",
      header: "Customer since",
      cell: (row) => sinceMonth(row.org.createdAt),
      compare: (a, b) => a.org.createdAt.localeCompare(b.org.createdAt),
    },
  ];

  const addButton = (
    <button type="button" class="button button-primary" onClick={() => setOpen(true)}>
      <Icon name="plus" />
      Add customer
    </button>
  );

  const firstRun = !loading && !error && orgs.length === 0;

  return (
    <div class="page">
      <PageHeader
        title="Customers"
        lede="Companies whose AWS accounts run the Nexus proxy. Open one to see its usage, users, sign-in and billing."
        actions={addButton}
      />

      {firstRun ? (
        <EmptyState
          title="No customers yet"
          lede="A customer is a company whose AWS account runs the Nexus proxy. Add one, then give it an installation, sign-in and a rate plan."
          steps={[
            { title: "Add the customer", text: "Its id must equal the org_id claim its developers' tokens carry." },
            { title: "Register an installation", text: "On the customer's AWS accounts tab; the proxy authenticates with its secret." },
            { title: "Set up sign-in and billing", text: "Choose who may sign in, then bill it on a rate plan from a month." },
          ]}
          actions={addButton}
        />
      ) : (
        <>
          <FilterBar>
            <Field id="customer-search" label="Search" help="common.search" compact>
              <span class="search-input">
                <Icon name="search" small />
                <input
                  id="customer-search"
                  type="search"
                  placeholder="Search by name or id"
                  value={search}
                  autocomplete="off"
                  onInput={(event) => setSearch(event.currentTarget.value)}
                />
              </span>
            </Field>
            <div class="field field-compact">
              <LabelText id="customer-status-label" label="Status" help="common.status" compact />
              <Segmented labelledBy="customer-status-label" small options={statusFilterOptions(counts)} value={status} onChange={(value: StatusFilter) => setStatus(value)} />
            </div>
          </FilterBar>

          <div class="table-card">
            <DataTable
              caption="Customers"
              hideCaption
              columns={columns}
              rows={rows}
              rowKey={(row) => row.org.id}
              loading={loading}
              error={error}
              onRetry={() => load(null, true)}
              emptyMessage={search.trim() || status !== "all" ? "No customer matches the search and status." : "No customers yet."}
              nextCursor={cursor}
              onLoadMore={() => load(cursor, false)}
            />
          </div>
        </>
      )}

      <Dialog
        title="Add customer"
        open={open}
        onClose={closeDialog}
        actions={
          <>
            <button type="button" class="button button-secondary" onClick={closeDialog}>
              Cancel
            </button>
            <button type="button" class="button button-primary" disabled={submitting} onClick={submit}>
              {submitting ? "Creating…" : "Create"}
            </button>
          </>
        }
      >
        <div class="stack">
          <Field id="org-name" label="Name" help="customers.name" error={errors.name}>
            <input
              id="org-name"
              value={form.name}
              aria-invalid={errors.name ? true : undefined}
              aria-describedby={errors.name ? errorId("org-name") : undefined}
              onInput={(event) => setForm({ ...form, name: event.currentTarget.value })}
              autocomplete="off"
            />
          </Field>
          <Field id="org-id" label="Id" help="customers.id" error={errors.id} hint="The org_id claim value; it cannot change later.">
            <input
              id="org-id"
              class="input-mono"
              value={form.id}
              aria-invalid={errors.id ? true : undefined}
              aria-describedby={errors.id ? errorId("org-id") : "org-id-hint"}
              onInput={(event) => setForm({ ...form, id: event.currentTarget.value })}
              autocomplete="off"
            />
          </Field>
          <Field id="org-slug" label="Slug" help="customers.slug" error={errors.slug}>
            <input
              id="org-slug"
              class="input-mono"
              value={form.slug}
              aria-invalid={errors.slug ? true : undefined}
              aria-describedby={errors.slug ? errorId("org-slug") : undefined}
              onInput={(event) => setForm({ ...form, slug: event.currentTarget.value })}
              autocomplete="off"
            />
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
